/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-serving.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * How the model is reached. All three speak the OpenAI chat-completions wire format, which is why
 * one adapter serves them and the backend selects an endpoint and a model rather than a client.
 * `llama_cpp` is GGUF on CPU inside the customer's network — the air-gapped default, and the one
 * most likely to fail `core.ai.evals`' capability bar, which is a product fact rather than a
 * defect. `hosted` is outside the customer's network and is **never** selectable for an air-gapped
 * tenant.
 */
export type ServingBackend = 'llama_cpp' | 'vllm' | 'hosted';

export const SERVING_BACKENDS: readonly ServingBackend[] = ['llama_cpp', 'vllm', 'hosted'];

export function isServingBackend(value: unknown): value is ServingBackend {
  return (['llama_cpp', 'vllm', 'hosted'] as readonly unknown[]).includes(value);
}

/**
 * Which feature is asking, and therefore which validator must run. Closed, and with **no
 * general-purpose member** — a general completion has no validator that could be written for it,
 * so it could only be gated by something that accepts anything, which is hard rule 8 satisfied on
 * paper and absent in fact. `copilot_answer` was missing here for one release while the route
 * iterated every member and returned it, so this authoritative spec was narrower than the API it
 * describes — the same defect `ai-evals.yaml` carried for `Capability`, and invisible for the same
 * reason: a generated envelope guard does not validate items. Adding a `Purpose` without adding it
 * here is now a failing test rather than a silent divergence.
 */
export type Purpose = 'pii_classification' | 'rule_suggestion' | 'anomaly_explanation' | 'copilot_answer';

export const PURPOSES: readonly Purpose[] = ['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'];

export function isPurpose(value: unknown): value is Purpose {
  return (['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'] as readonly unknown[]).includes(value);
}

/**
 * Why a validator refused a completion. A closed vocabulary because these are **counted**:
 * `core.ai.evals`' per-backend capability matrix is built from this distribution, and a model that
 * invents columns cannot be trusted with a feature while one that is merely slow can be retried.
 * `content_filtered` and `provider_error` are deliberately separate: the model's own safety layer
 * declining is *their* behaviour, an unreachable endpoint is *ours*. `core.ai.evals` exempts our
 * outages from a capability bar and must not exempt their refusals, or declining to answer becomes
 * a way for a model to stay unproven and therefore permitted. `ungrounded` is the one that matters
 * most — output referring to something not in the input. An invented column attaches a verdict to
 * an entity nobody can open while displacing the real one, which on a PII screen means a column of
 * Aadhaar numbers reported clean.
 */
export type RejectionReason = 'truncated' | 'malformed' | 'out_of_vocabulary' | 'ungrounded' | 'out_of_range' | 'empty' | 'content_filtered' | 'provider_error';

export const REJECTION_REASONS: readonly RejectionReason[] = ['truncated', 'malformed', 'out_of_vocabulary', 'ungrounded', 'out_of_range', 'empty', 'content_filtered', 'provider_error'];

export function isRejectionReason(value: unknown): value is RejectionReason {
  return (['truncated', 'malformed', 'out_of_vocabulary', 'ungrounded', 'out_of_range', 'empty', 'content_filtered', 'provider_error'] as readonly unknown[]).includes(value);
}

export interface PurposeEntry {
  readonly code: Purpose;
  /** The validator that must accept a completion for this purpose. */
  readonly validated_by: string;
}

export function isPurposeEntry(value: unknown): value is PurposeEntry {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'] as readonly unknown[]).includes(candidate['code']) &&
    typeof candidate['validated_by'] === 'string'
  );
}

export interface ServingCapabilities {
  /** ADR-004's ladder position. Always 1 — metadata only, never a row. */
  readonly rung: number;
  readonly purposes: readonly PurposeEntry[];
  /**
   * What the AI plane will never put in a prompt. Read from the deployment rather than written
   * here, so it cannot drift from the enum under review.
   */
  readonly never_sent: readonly string[];
  readonly rejection_reasons: readonly RejectionReason[];
  readonly max_prompt_chars: number;
  readonly validator_policy: string;
}

export function isServingCapabilities(value: unknown): value is ServingCapabilities {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rung'] === 'number' &&
    Array.isArray(candidate['purposes']) &&
    (candidate['purposes'] as readonly unknown[]).every((item) => isPurposeEntry(item)) &&
    Array.isArray(candidate['never_sent']) &&
    (candidate['never_sent'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['rejection_reasons']) &&
    (candidate['rejection_reasons'] as readonly unknown[]).every((item) => isRejectionReason(item)) &&
    typeof candidate['max_prompt_chars'] === 'number' &&
    typeof candidate['validator_policy'] === 'string'
  );
}

export interface ServingRouteView {
  /**
   * `false` with a `note` when no model is routed. Deliberately not an empty route object: "nobody
   * set this up" and "somebody switched it off" are different facts, and the second is
   * `configured: true, enabled: false`.
   */
  readonly configured: boolean;
  readonly note?: string;
  readonly backend?: ServingBackend;
  readonly endpoint?: string;
  readonly model?: string;
  readonly air_gapped?: boolean;
  readonly enabled?: boolean;
}

export function isServingRouteView(value: unknown): value is ServingRouteView {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['configured'] === 'boolean'
  );
}
